'use client'

import { cn } from '@/lib/utils'
import * as React from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'
import { Label } from './label'

interface ReadOnlyInputProps extends Omit<React.ComponentProps<'input'>, 'readOnly' | 'disabled'> {
	tooltipText?: string
	label?: string
	subText?: string
}

function ReadOnlyInput({ className, tooltipText, label, subText, ...props }: ReadOnlyInputProps) {
	const [isHovered, setIsHovered] = React.useState(false)

	const inputElement = (
		<input
			type="text"
			readOnly
			data-slot="readonly-input"
			className={cn(
				'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium cursor-default',
				'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
				'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
				className
			)}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			{...props}
		/>
	)

	const wrappedInput = tooltipText ? (
		<Tooltip>
			<TooltipTrigger asChild>
				{inputElement}
			</TooltipTrigger>
			<TooltipContent side="bottom" className="max-w-xs">
				{tooltipText}
			</TooltipContent>
		</Tooltip>
	) : inputElement

	const inputContainer = (
		<div className="space-y-1">
			{wrappedInput}
			{/* Reserved space for sub-text - always present to maintain consistent height */}
			<div className="h-5 flex items-start">
				{subText && (
					<p 
						className={cn(
							"text-xs text-gray-500 transition-opacity duration-200",
							isHovered ? "opacity-100" : "opacity-0"
						)}
					>
						{subText}
					</p>
				)}
			</div>
		</div>
	)

	if (label) {
		return (
			<div className="space-y-2">
				<Label>{label}</Label>
				{inputContainer}
			</div>
		)
	}

	return inputContainer
}

export { ReadOnlyInput }